import React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { Input } from "~/react/ui/input"
import { cn } from "~/lib/utils"

const numberPickerVariants = cva("h-qty-selector", {
  variants: {
    size: {
      xs: "h-qty-selector--xs",
      sm: "h-qty-selector--sm",
    },
  },
})

export interface NumberPickerProps
  extends VariantProps<typeof numberPickerVariants> {
  value: number
  onChange: (next: number) => void
  min?: number
  max?: number
  step?: number
  ariaLabelPrefix?: string
  className?: string
}

export const NumberPicker: React.FC<NumberPickerProps> = ({
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  ariaLabelPrefix = "Quantity",
  size,
  className,
}) => {
  const canDecrease = value > min
  const canIncrease = typeof max === "number" ? value < max : true

  const decrease = () => {
    const next = Math.max(min, value - step)
    if (next !== value) onChange(next)
  }

  const increase = () => {
    const next =
      typeof max === "number" ? Math.min(max, value + step) : value + step
    if (next !== value) onChange(next)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseInt(e.target.value, 10)
    if (!isNaN(newValue)) {
      const clampedValue =
        typeof max === "number"
          ? Math.min(max, Math.max(min, newValue))
          : Math.max(min, newValue)
      onChange(clampedValue)
    }
  }

  return (
    <div className={cn(numberPickerVariants({ size }), className)}>
      <Button
        type="button"
        className="h-qty-selector__btn"
        size="xs"
        variant="ghost"
        aria-label={`${ariaLabelPrefix}: decrease`}
        onClick={decrease}
        disabled={!canDecrease}
      >
        <Icon icon="minus" size="sm" aria-hidden="true" />
      </Button>
      <Input
        className="h-qty-selector__input"
        value={value}
        type="number"
        min={min}
        max={max}
        onChange={handleInputChange}
        aria-label={ariaLabelPrefix}
        data-1p-ignore
        data-bwignore
      />
      <Button
        type="button"
        className="h-qty-selector__btn"
        size="xs"
        variant="ghost"
        aria-label={`${ariaLabelPrefix}: increase`}
        onClick={increase}
        disabled={!canIncrease}
      >
        <Icon icon="plus" size="sm" aria-hidden="true" />
      </Button>
    </div>
  )
}
